/**
 * Casuki — comportamiento de las secciones «Arena».
 *
 * <casuki-catalog> filtra por colección y reordena por precio/nombre sin recargar.
 * Los datos vienen del marcado que genera Liquid: cada tarjeta lleva
 * data-collections (handles separados por espacio), data-price y data-title.
 */

class CasukiCatalog extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-casuki-grid]');
    if (!this.grid) return;

    this.tabs = Array.from(this.querySelectorAll('[data-casuki-tab]'));
    this.sortSelect = this.querySelector('[data-casuki-sort]');
    this.emptyState = this.querySelector('[data-casuki-empty]');
    this.countLabel = this.querySelector('[data-casuki-count]');

    this.cards = Array.from(this.grid.querySelectorAll('[data-casuki-card]'));
    // Orden original: lo que decidió el merchant en la colección de Shopify.
    this.cards.forEach((card, index) => {
      card.dataset.casukiIndex = String(index);
    });

    this.activeFilter = 'all';

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.activeFilter = tab.dataset.casukiTab || 'all';
        this.tabs.forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
        this.apply();
      });
    });

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => this.apply());
    }

    this.apply();
  }

  apply() {
    const filter = this.activeFilter;
    let visible = 0;

    for (const card of this.cards) {
      const handles = (card.dataset.collections || '').split(' ').filter(Boolean);
      const matches = filter === 'all' || handles.includes(filter);
      card.hidden = !matches;
      if (matches) visible += 1;
    }

    this.sort();

    if (this.emptyState) this.emptyState.hidden = visible !== 0;
    if (this.countLabel) {
      this.countLabel.textContent = visible === 1 ? '1 producto' : `${visible} productos`;
    }
  }

  sort() {
    const mode = this.sortSelect ? this.sortSelect.value : 'featured';

    const sorted = [...this.cards].sort((a, b) => {
      switch (mode) {
        case 'price-asc':
          return Number(a.dataset.price || 0) - Number(b.dataset.price || 0);
        case 'price-desc':
          return Number(b.dataset.price || 0) - Number(a.dataset.price || 0);
        case 'title-asc':
          return (a.dataset.title || '').localeCompare(b.dataset.title || '', 'es');
        default:
          return Number(a.dataset.casukiIndex || 0) - Number(b.dataset.casukiIndex || 0);
      }
    });

    // Un solo reflow: reordenamos con un fragmento en lugar de N appendChild sueltos.
    const fragment = document.createDocumentFragment();
    for (const card of sorted) fragment.appendChild(card);
    this.grid.appendChild(fragment);
  }
}

if (!customElements.get('casuki-catalog')) {
  customElements.define('casuki-catalog', CasukiCatalog);
}

/* --- Animación de entrada ------------------------------------------------- */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      )
    : null;

function initReveals(root = document) {
  const targets = root.querySelectorAll('.casuki-reveal:not(.is-visible)');

  for (const el of targets) {
    // Lo que ya está en pantalla al cargar no debe parpadear.
    if (!revealObserver || el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      el.classList.add('is-visible');
      continue;
    }
    revealObserver.observe(el);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initReveals());
} else {
  initReveals();
}

// El editor de temas reemplaza el HTML de la sección: hay que re-inicializar.
document.addEventListener('shopify:section:load', (event) => {
  initReveals(event.target);
});

/* --- Botón "Volver" -------------------------------------------------------- */

/**
 * Usa el historial si venimos de dentro del sitio (así vuelve exactamente a
 * la colección o filtro desde donde se navegó). Si se entró por enlace
 * externo o directo, no hay a dónde volver: va al inicio.
 */
document.addEventListener('click', (event) => {
  const back = event.target.closest('[data-casuki-back]');
  if (!back) return;

  let sameOriginReferrer = false;
  try {
    sameOriginReferrer = document.referrer && new URL(document.referrer).origin === window.location.origin;
  } catch {
    sameOriginReferrer = false;
  }

  if (sameOriginReferrer && window.history.length > 1) {
    window.history.back();
  } else {
    window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
});

/* --- Puente con el carrito del tema --------------------------------------- */

/**
 * El botón de carrito del encabezado Casuki es un enlace real a /cart, así que
 * funciona siempre. Si el tema tiene el drawer activo, se abre/cierra
 * llamando a su propio método público (theme-drawer#toggle) en vez de
 * reimplementar la apertura: lo que haga Horizon (animación, foco,
 * accesibilidad) se respeta tal cual.
 *
 * toggle() y no open(): con open() a secas, un segundo clic sobre el
 * mismo botón mientras el drawer ya está abierto no lo cerraba — se
 * quedaba invasivo en pantalla sin forma de cerrarlo desde ahí.
 *
 * Nota: el encabezado de Casuki no usa el header-actions nativo de
 * Horizon, así que el botón `[data-testid="cart-drawer-trigger"]` que
 * Horizon espera nunca existe en este tema. Por eso se invoca el drawer
 * directamente por su elemento (#cart-drawer es un <theme-drawer>), en
 * vez de buscar un disparador que nunca se va a encontrar.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-casuki-cart-trigger]');
  if (!trigger || event.defaultPrevented) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const drawer = document.getElementById('cart-drawer');
  if (!drawer || typeof drawer.toggle !== 'function') return; // Sin drawer: que navegue a /cart normalmente.

  event.preventDefault();
  drawer.toggle();
});

/**
 * El contador del encabezado se suscribe al store (casuki-cart-store.js,
 * cargado antes desde casuki-base.liquid). Es la única fuente de verdad:
 * cualquier cambio de cantidad en cualquier tarjeta lo actualiza al instante.
 */
function initCartCount() {
  const store = window.CasukiCart && window.CasukiCart.cartStore;
  if (!store) return;

  const render = () => {
    for (const node of document.querySelectorAll('[data-casuki-cart-count]')) {
      node.textContent = String(store.itemCount);
      node.toggleAttribute('hidden', store.itemCount === 0);
      node.classList.remove('is-popped');
      void node.offsetWidth;
      node.classList.add('is-popped');
    }
  };

  store.addEventListener('casuki:cart-change', render);
  render();
}

initCartCount();

/* --- Barra flotante de carrito ---------------------------------------------- */

/**
 * A diferencia del contador del encabezado, aquí NO se sincroniza de
 * inmediato al cargar: el store todavía no terminó de hidratarse en ese
 * instante (itemCount arranca en 0 hasta que resuelve el fetch a /cart.js),
 * y hacerlo causaría que una barra ya visible (pintada por Liquid con el
 * carrito real) parpadee oculta y vuelva a aparecer. Se deja el primer
 * pintado tal como lo dejó Liquid, y solo se reacciona a partir del primer
 * cambio real que reporte el store.
 */
function initCartBar() {
  const store = window.CasukiCart && window.CasukiCart.cartStore;
  const bar = document.querySelector('[data-casuki-cartbar]');
  if (!store || !bar) return;

  const countEl = bar.querySelector('[data-casuki-cartbar-count]');
  let shown = !bar.hidden;

  if (shown) {
    requestAnimationFrame(() => bar.classList.add('is-visible'));
  }

  store.addEventListener('casuki:cart-change', () => {
    const count = store.itemCount;

    countEl.textContent = String(count);
    countEl.classList.remove('is-popped');
    void countEl.offsetWidth;
    countEl.classList.add('is-popped');

    if (count > 0 && !shown) {
      shown = true;
      bar.hidden = false;
      requestAnimationFrame(() => bar.classList.add('is-visible'));
    } else if (count === 0 && shown) {
      shown = false;
      bar.classList.remove('is-visible');
      setTimeout(() => {
        bar.hidden = true;
      }, 240);
    }
  });
}

initCartBar();

/* --- Cuenta regresiva (Oferta flash) ---------------------------------------- */

class CasukiCountdown extends HTMLElement {
  connectedCallback() {
    this.cyclic = this.dataset.cycle === 'true';

    if (this.cyclic) {
      // Ciclo automático: el servidor ya calculó cuándo termina la ventana activa actual.
      const clockTarget = Number(this.dataset.deadlineClock);
      if (!clockTarget) return;
      this.deadline = new Date(clockTarget * 1000);
    } else {
      // El merchant escribe "2026-08-10 23:59" en hora de Colombia (UTC-5).
      const raw = this.dataset.deadline;
      if (!raw) return;
      this.deadline = new Date(`${raw.trim().replace(' ', 'T')}:00-05:00`);
    }

    if (Number.isNaN(this.deadline.getTime())) return;

    this.parts = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };

    if (!this.parts.days || !this.parts.hours || !this.parts.minutes || !this.parts.seconds) return;

    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const remaining = this.deadline - Date.now();

    if (remaining <= 0) {
      clearInterval(this.timer);

      if (this.cyclic) {
        // Termina esta ventana del ciclo: se oculta toda la sección hasta la próxima.
        const flash = this.closest('.casuki-flash');
        if (flash) flash.hidden = true;
        return;
      }

      this.dataset.expired = 'true'; // el CSS oculta el reloj y muestra el aviso
      return;
    }

    const total = Math.floor(remaining / 1000);
    const pad = (n) => String(n).padStart(2, '0');

    this.parts.days.textContent = pad(Math.floor(total / 86400));
    this.parts.hours.textContent = pad(Math.floor(total / 3600) % 24);
    this.parts.minutes.textContent = pad(Math.floor(total / 60) % 60);
    this.parts.seconds.textContent = pad(total % 60);
  }
}

if (!customElements.get('casuki-countdown')) {
  customElements.define('casuki-countdown', CasukiCountdown);
}

/* --- Botón flotante de WhatsApp --------------------------------------------- */

function initWhatsappFloat() {
  const root = document.querySelector('[data-casuki-wa]');
  if (!root) return;

  const DISMISS_KEY = 'casukiWaDismissed';
  const bubble = root.querySelector('[data-casuki-wa-bubble]');
  const delaySeconds = Number(root.dataset.delay || 3);

  let shown = false;

  const reveal = () => {
    if (shown) return;
    shown = true;
    root.hidden = false;
    // El navegador necesita un frame con [hidden] ya fuera para animar la entrada.
    requestAnimationFrame(() => root.classList.add('is-visible'));

    if (bubble && localStorage.getItem(DISMISS_KEY) !== 'true') {
      setTimeout(() => {
        bubble.hidden = false;
        requestAnimationFrame(() => bubble.classList.add('is-visible'));
        setTimeout(() => hideBubble(), 8000);
      }, 1200);
    }

    window.removeEventListener('scroll', onScroll);
  };

  const hideBubble = () => {
    if (!bubble) return;
    bubble.classList.remove('is-visible');
    setTimeout(() => {
      bubble.hidden = true;
    }, 240);
  };

  const onScroll = () => {
    if (window.scrollY > 300) reveal();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  setTimeout(reveal, delaySeconds * 1000);

  const dismissBtn = root.querySelector('[data-casuki-wa-dismiss]');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      localStorage.setItem(DISMISS_KEY, 'true');
      hideBubble();
    });
  }

  // El drawer tapa la esquina inferior derecha con el botón de pagar: el
  // flotante estorba justo en el momento de mayor intención de compra.
  const drawer = document.getElementById('cart-drawer');
  if (drawer) {
    const syncWithDrawer = () => {
      root.classList.toggle('is-visible', shown && !drawer.hasAttribute('open'));
    };
    new MutationObserver(syncWithDrawer).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWhatsappFloat);
} else {
  initWhatsappFloat();
}

/* --- Ventana de captura de correo ------------------------------------------- */

function initCaptureModal() {
  const root = document.querySelector('[data-casuki-capture]');
  if (!root) return;

  const DISMISS_KEY = 'casukiCaptureDismissedUntil';
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const ONE_YEAR_MS = THIRTY_DAYS_MS * 12;

  /**
   * El formulario se envía de forma nativa (recarga completa de página):
   * es el único método que /contact acepta sin exigir un token de CAPTCHA
   * que un envío por fetch no puede aportar. Si el marcador que Liquid
   * pintó tras el POST está presente, esta es esa recarga: se reabre el
   * modal directo en su estado de éxito, sin esperar ningún disparador.
   */
  const justPosted = !!root.querySelector('[data-casuki-capture-posted]');

  const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!justPosted && Date.now() < dismissedUntil) return; // ya lo cerró hace poco, no insistir

  const form = root.querySelector('.casuki-capture__form');
  const consentBox = root.querySelector('[data-casuki-capture-consent]');
  const submitBtn = form ? form.querySelector('.casuki-capture__submit') : null;

  const delaySeconds = Number(root.dataset.delay || 25);
  const scrollPercent = Number(root.dataset.scrollPercent || 60);
  const isTouchDevice = window.matchMedia('(hover: none), (pointer: coarse)').matches;

  let shown = false;

  const reveal = () => {
    if (shown) return;
    shown = true;

    root.hidden = false;
    requestAnimationFrame(() => root.classList.add('is-visible'));

    document.addEventListener('keydown', onKeydown);

    // El campo de correo recibe el foco: quien navega con teclado no se pierde.
    const emailInput = root.querySelector('input[type="email"]');
    if (emailInput) setTimeout(() => emailInput.focus(), 260);

    detachTriggers();
  };

  const dismiss = () => {
    root.classList.remove('is-visible');
    localStorage.setItem(DISMISS_KEY, String(Date.now() + THIRTY_DAYS_MS));
    setTimeout(() => {
      root.hidden = true;
    }, 240);
    document.removeEventListener('keydown', onKeydown);
  };

  const onKeydown = (event) => {
    if (event.key === 'Escape') dismiss();
  };

  // Salida: el cursor sube hacia la barra de direcciones. Solo en escritorio,
  // y solo tras unos segundos reales en la página (evita falsos positivos al
  // entrar con el mouse ya arriba).
  let pageEnteredAt = Date.now();
  const onExitIntent = (event) => {
    if (Date.now() - pageEnteredAt < 4000) return;
    if (event.clientY <= 0) reveal();
  };

  const onScroll = () => {
    const scrolled = window.scrollY;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    if ((scrolled / max) * 100 >= scrollPercent) reveal();
  };

  const delayTimer = setTimeout(reveal, delaySeconds * 1000);

  function detachTriggers() {
    clearTimeout(delayTimer);
    document.removeEventListener('mouseleave', onExitIntent);
    window.removeEventListener('scroll', onScroll);
  }

  if (isTouchDevice) {
    window.addEventListener('scroll', onScroll, { passive: true });
  } else {
    document.addEventListener('mouseleave', onExitIntent);
  }

  for (const closeBtn of root.querySelectorAll('[data-casuki-capture-close]')) {
    closeBtn.addEventListener('click', dismiss);
  }

  // La casilla de consentimiento manda: sin marcarla no hay envío posible,
  // sin importar lo que decida el formulario nativo de Shopify.
  if (consentBox && submitBtn) {
    submitBtn.disabled = !consentBox.checked;
    consentBox.addEventListener('change', () => {
      submitBtn.disabled = !consentBox.checked;
    });
  }

  if (justPosted) {
    // Ya se suscribió: no tiene sentido volver a insistir en 30 días, ni en 30.
    localStorage.setItem(DISMISS_KEY, String(Date.now() + ONE_YEAR_MS));
    reveal();
    setTimeout(dismiss, 3200);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCaptureModal);
} else {
  initCaptureModal();
}

/* --- Página de contacto: arma el mensaje y abre WhatsApp -------------------- */

/**
 * No es un formulario que se envíe a un servidor: solo compone el texto con
 * lo que la persona escribió y abre wa.me con eso ya redactado. Al no tocar
 * /contact, no depende de que Shopify acepte el envío (ver el problema de
 * CAPTCHA que bloquea el formulario nativo de boletín).
 */
document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-casuki-wa-form]');
  if (!form) return;

  event.preventDefault();

  const number = form.dataset.waNumber;
  const name = form.elements.name.value.trim();
  const message = form.elements.message.value.trim();
  if (!number || !name || !message) return;

  const text = `Hola, soy ${name}. ${message}`;
  window.open(`https://wa.me/${number}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
});

/* --- Página de gracias: productos al azar ----------------------------------- */

/**
 * Baraja las tarjetas ya renderizadas por Liquid (Fisher-Yates) y muestra
 * solo las primeras N. El "pool" completo llega en el HTML; aquí solo se
 * decide el orden y qué se oculta, sin pedir nada al servidor.
 */
class CasukiShuffle extends HTMLElement {
  connectedCallback() {
    const cards = Array.from(this.querySelectorAll('[data-casuki-card]'));
    if (cards.length === 0) return;

    for (let i = cards.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }

    const fragment = document.createDocumentFragment();
    for (const card of cards) fragment.appendChild(card);
    this.appendChild(fragment);

    const show = Number(this.dataset.show || 4);
    cards.forEach((card, index) => {
      card.hidden = index >= show;
    });
  }
}

if (!customElements.get('casuki-shuffle')) {
  customElements.define('casuki-shuffle', CasukiShuffle);
}

/* --- Alto real del encabezado fijo ------------------------------------------ */

/**
 * El encabezado usa position:fixed (ver sections/casuki-header.liquid),
 * así que sale del flujo normal y hay que reservarle el espacio a mano
 * en <main>. Se mide el alto real en vez de asumir uno fijo: cambia
 * según el ancho de pantalla (el menú puede pasar a una segunda línea)
 * y según si las fuentes de Casuki ya cargaron o no.
 */
function syncCasukiHeaderHeight() {
  const header = document.querySelector('.casuki-header--sticky');
  if (!header) return;

  const set = () => {
    document.documentElement.style.setProperty('--casuki-header-height', `${header.offsetHeight}px`);
  };

  set();
  window.addEventListener('resize', set);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(set);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncCasukiHeaderHeight);
} else {
  syncCasukiHeaderHeight();
}
